import _ from "underscore";
import React from "react";
import {Paper, Tooltip} from "@material-ui/core";
import WorkflowEditor from "common_components/issue_editor/components/workflow_editor";
import OverridableIssueFields from "utils/overridable_issue_fields";
import {Issue, IssueOverrideValues} from "common/types/issue";
import {OrganisationId} from "common/types/organisation";
import {WorkflowSettings} from "common/flowmaster/types/workflow";
import {PlaybookIssueset} from "common/types/issueset";

function formatWorkflowInputValues(workflowSettings?: WorkflowSettings) {
  if (!workflowSettings?.config) {
    return {};
  }

  return Object.values(workflowSettings.config).reduce(
    (allInputs, workflow) => {
      const values = workflow?.inputs?.values;
      if (!values) {
        return allInputs;
      }

      for (const [key, value] of Object.entries(values)) {
        allInputs[`input_${key}`] = value;
      }

      return allInputs;
    },
    {} as Record<string, unknown>,
  );
}

export interface OverridableFields {
  main: {
    issue: Omit<Issue, "usage">;
    display_name: string;
    llm_settings: Issue["llm_settings"];
    inputs: Record<string, unknown>;
  };
  overridden: IssueOverrideValues;
}

interface PromptEditorProps {
  issue: Issue;
  organisationId: OrganisationId;
  selectedIssueset?: PlaybookIssueset;
  onIssueUpdate: (issue: Issue, update: Partial<Issue>) => void;
  canEditWorkflowInputOverrides: boolean;
}

interface PromptEditorState {
  overridableFields: OverridableFields;
  selectedIssueset?: PlaybookIssueset;
}

class PromptEditor extends OverridableIssueFields<
  PromptEditorProps,
  PromptEditorState
> {
  constructor(props: PromptEditorProps) {
    super(props);
    this.state = {
      ...this.state,
      overridableFields: this.getOverridableFields(),
      selectedIssueset: props.selectedIssueset,
    };
  }

  getOverridableFields = () => {
    const {issue} = this.props;
    return {
      main: {
        issue: _.omit(issue, ["usage"]),
        display_name: issue.display_name,
        llm_settings: issue.llm_settings,
        inputs: formatWorkflowInputValues(issue.llm_settings.workflow_settings),
      },
      overridden: issue.override_values,
    };
  };

  componentDidUpdate(prevProps: PromptEditorProps) {
    if (
      prevProps.issue.id !== this.props.issue.id ||
      prevProps.selectedIssueset !== this.props.selectedIssueset
    ) {
      this.setState({
        overridableFields: this.getOverridableFields(),
        selectedIssueset: this.props.selectedIssueset,
      });
    }
  }

  onOverridableIssueFieldUpdate(update: Partial<Issue>): void {
    this.props.onIssueUpdate(this.props.issue, update);
  }

  updateLlmSettings = (newSettings: Partial<Issue["llm_settings"]>): void => {
    const updatedSettings = {
      ...this.props.issue.llm_settings,
      ...newSettings,
    };
    this.onOverridableIssueFieldUpdate({llm_settings: updatedSettings});
  };

  renderWorkflowEditor() {
    const {issue, organisationId, canEditWorkflowInputOverrides} = this.props;

    const editorContent = (
      <WorkflowEditor
        issueId={issue.id}
        organisationId={organisationId}
        llmAnalysisType={issue.llm_analysis_type}
        llm_settings={issue.llm_settings}
        updateLlmSettings={this.updateLlmSettings}
        getOverridableFieldData={this.getOverridableFieldData.bind(this)}
        isOverrideMode={this.isOverrideModeActive()}
        isDisplayedInPlaybook
      />
    );

    if (!canEditWorkflowInputOverrides) {
      return (
        <div style={{cursor: "not-allowed"}}>
          <Tooltip
            title="You do not have permission to edit workflow input overrides"
            placement="bottom"
          >
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "100%",
              }}
            >
              <div
                style={{
                  pointerEvents: "none",
                  opacity: 0.8,
                }}
              >
                {editorContent}
              </div>
            </div>
          </Tooltip>
        </div>
      );
    }

    return editorContent;
  }

  render() {
    const {issue} = this.props;
    return (
      <Paper style={{padding: "2rem 1rem", maxWidth: "100%"}}>
        {issue.trigger_type === "LLM" &&
          issue.llm_settings.use_production_mode &&
          this.renderWorkflowEditor()}
      </Paper>
    );
  }
}

export default PromptEditor;
